import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusinessType } from '@/contexts/BusinessTypeContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Phone, MapPin, Package, DollarSign, Clock, 
  CheckCircle2, XCircle, ChefHat, Truck, AlertCircle, CreditCard,
  Calendar, MessageSquare
} from 'lucide-react';
import { OrderStatusBadge, formatCurrency, formatDateTime } from '@/lib/utils-helpers';
import OrderWorkflowProgress from '@/components/vendor/OrderWorkflowProgress';

export default function VendorOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { config } = useBusinessType();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await api.get(`/vendor/orders/${orderId}`);
      setOrder(res.data);
    } catch (err) {
      toast.error('Failed to load order details');
      navigate('/vendor/orders');
    } finally {
      setLoading(false);
    }
  };

  const performAction = async (action, label) => {
    setProcessing(true);
    try {
      await api.put(`/vendor/orders/${orderId}/${action}`);
      toast.success(`Order ${label}`);
      fetchOrder(); // Reload to get updated status
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${label}`);
    } finally {
      setProcessing(false);
    }
  };

  // Workflow timeline steps
  const getWorkflowTimeline = () => {
    const steps = [
      { status: 'placed', label: 'Order Placed', icon: Package, description: 'Customer placed the order' },
      { status: 'confirmed', label: 'Confirmed', icon: CheckCircle2, description: 'You accepted the order' },
      { status: 'preparing', label: 'Preparing', icon: ChefHat, description: 'Order is being prepared' },
      { status: 'ready', label: 'Ready', icon: Package, description: 'Order is ready for pickup' },
      { status: 'out_for_delivery', label: 'Out for Delivery', icon: Truck, description: 'Driver is on the way' },
      { status: 'delivered', label: 'Delivered', icon: CheckCircle2, description: 'Order delivered to customer' },
    ];

    const currentStatusIndex = steps.findIndex(s => s.status === order?.status);
    
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentStatusIndex,
      active: index === currentStatusIndex,
      pending: index > currentStatusIndex
    }));
  };

  // Get available actions based on current status
  const getAvailableActions = () => {
    if (!order) return [];
    
    const actions = [];
    if (order.status === 'placed') {
      actions.push({ action: 'accept', label: 'Accept Order', icon: CheckCircle2, variant: 'default' });
      actions.push({ action: 'decline', label: 'Decline Order', icon: XCircle, variant: 'destructive' });
    }
    if (order.status === 'confirmed') {
      actions.push({ action: 'prepare', label: 'Start Preparing', icon: ChefHat, variant: 'default' });
    }
    if (order.status === 'preparing') {
      actions.push({ action: 'ready', label: 'Mark as Ready', icon: Package, variant: 'default' });
    }
    return actions;
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!order) {
    return <div className="p-6">Order not found</div>;
  }

  const timeline = getWorkflowTimeline();
  const actions = getAvailableActions();
  const customerName = order.user_name || order.customer_name || 'Customer';
  const items = order.items || [];
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryFee = order.delivery_fee || 0;
  const tax = order.tax || 0;
  const total = order.total || (subtotal + deliveryFee + tax);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vendor/orders')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="order-detail-title">
              {config.orderLabel} #{order.order_number || order.id?.substring(0, 8).toUpperCase()}
            </h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDateTime(order.created_at)}
              </span>
              <OrderStatusBadge status={order.status} />
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        {actions.length > 0 && (
          <div className="flex gap-2">
            {actions.map(act => {
              const Icon = act.icon;
              return (
                <Button
                  key={act.action}
                  variant={act.variant}
                  onClick={() => performAction(act.action, act.label)}
                  disabled={processing}
                  data-testid={`order-action-${act.action}`}
                >
                  <Icon className="h-4 w-4 mr-2" /> {act.label}
                </Button>
              );
            })}
          </div>
        )}
      </div>

      {/* Alert for pending action */}
      {order.status === 'placed' && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Action Required:</strong> This order is waiting for your confirmation. Please accept or decline.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Workflow Progress */}
          <Card>
            <CardHeader>
              <CardTitle>{config.orderLabel} Progress & Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <OrderWorkflowProgress 
                order={order} 
                onStatusChange={(result) => {
                  toast.success('Order status updated');
                  fetchOrder(); // Reload order
                }}
              />
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle>{config.orderLabel} Items ({items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        <Badge variant="outline" className="text-xs">x{item.quantity}</Badge>
                      </div>
                      {item.modifiers && item.modifiers.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Add-ons: {item.modifiers.join(', ')}
                        </p>
                      )}
                      {item.special_instructions && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Note: {item.special_instructions}
                        </p>
                      )}
                    </div>
                    <span className="font-medium tabular-nums">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              {/* Order Summary */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Delivery Fee</span>
                  <span>{formatCurrency(deliveryFee)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
                <Separator className="my-2" />
                <div className="flex items-center justify-between text-base font-semibold">
                  <span>Total</span>
                  <span className="text-lg">{formatCurrency(total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Special Instructions */}
          {order.special_instructions && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Special Instructions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{order.special_instructions}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {config.customerLabel}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-medium">{customerName}</p>
                {order.customer_email && (
                  <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                )}
              </div>
              
              {order.customer_phone && (
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">{order.customer_phone}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.delivery_address ? (
                <div className="space-y-1">
                  <p className="text-sm">{order.delivery_address.street}</p>
                  {order.delivery_address.apartment && (
                    <p className="text-sm">{order.delivery_address.apartment}</p>
                  )}
                  <p className="text-sm">
                    {order.delivery_address.city}, {order.delivery_address.state} {order.delivery_address.zip_code}
                  </p>
                  {order.delivery_address.instructions && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Instructions: {order.delivery_address.instructions}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No address provided</p>
              )}
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Method</span>
                <span className="font-medium">{order.payment_method || 'Card'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                  {order.payment_status || 'Paid'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Timing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Timing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Placed</span>
                <span>{formatDateTime(order.created_at)}</span>
              </div>
              {order.confirmed_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Confirmed</span>
                  <span>{formatDateTime(order.confirmed_at)}</span>
                </div>
              )}
              {order.estimated_delivery && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Est. Delivery</span>
                  <span>{formatDateTime(order.estimated_delivery)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
